import BusinessPartnerAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/business-partners/business-partner/business-partner.entity';
import { CommonModule } from '@angular/common';
import { Component, OnInit, ChangeDetectorRef, DestroyRef, Input, OnDestroy, ViewChild, AfterViewInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, FormControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogContent, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { TranslocoModule } from '@ngneat/transloco';
import { BusinessPartnerService } from 'app/modules/components/business-partners/business-partner/business-partner.service';
import { ConfigurationFiltersComponent } from 'app/modules/components/filters/configuration-filters/configuration-filters.component';
import { getCode } from 'app/utils/method';
import { MatIconModule } from '@angular/material/icon';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PartnerChainIds } from '../dto/PartnerChainIds.interface';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { MatOptionModule } from '@angular/material/core';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { Subject } from 'rxjs';
import { PriceListService } from '../../price-list.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import BusinessPartnerGroupAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/business-partners/business-partner-group/business-partner-group.entity';
import { IRequestResult } from 'app/common/interfaces/request-result.interface';
import { BusinessPartnerGroupService } from 'app/modules/components/business-partners/business-partner-group/business-partner-group.service';

@Component({
  selector: 'app-add-customers-pricelist-component',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatRadioModule,
    MatSelectModule,
    MatDialogContent,
    ConfigurationFiltersComponent,
    MatTableModule,
    MatCheckboxModule,
    TranslocoModule,
    MatIconModule,
    NgxMatSelectSearchModule,
    MatOptionModule,
    MatPaginatorModule
  ],
  templateUrl: './add-customers-pricelist-component.component.html',
  styleUrl: './add-customers-pricelist-component.component.scss'
})
export class AddCustomersPricelistComponentComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() priceListId: string;

  dataSource = new MatTableDataSource<BusinessPartnerAttributes>([]);
  filteredCustomers: BusinessPartnerAttributes[] = [];
  customerSelectControl = new FormControl<BusinessPartnerAttributes[]>([]);
  customerSearchControl = new FormControl<string>('');

  customerSelectControlByGroup = new FormControl<BusinessPartnerGroupAttributes[]>([]);

  businessGroupList: BusinessPartnerGroupAttributes[] = [];
  filteredBusinessGroupList: BusinessPartnerGroupAttributes[] = [];

  form: FormGroup;
  displayedColumns: string[] = ["name", "partnerGroup", "actions"];

  @ViewChild('paginator') paginator!: MatPaginator;

  geolocationFilter: "both" | "not_registered" | "valid" = "both";
  
  private selectedCustomersSubject = new Subject<BusinessPartnerAttributes[]>();

  constructor(
    private readonly businessService: BusinessPartnerService,
    private _changeDetectorRef: ChangeDetectorRef,
    private fb: FormBuilder,
    private destroyRef: DestroyRef,
    private priceListService: PriceListService,
    private dialogRef: MatDialogRef<AddCustomersPricelistComponentComponent>,
    private snackBar: MatSnackBar,
    private readonly businessGroupService: BusinessPartnerGroupService,
  ) {
    this.form = this.fb.group({
      customerSelectionType: ['all'],
      specificCustomers: [[]],
      distributionChainId: [null, Validators.required],
      customersSelectedsBy: ['customers'],
      businessPartnerGroupId: [],
      searchBusinessPartenertGroup: ['']
    });
  }

  getCode = getCode;

  ngOnInit(): void {
    const specificCustomersControl = this.form.get('specificCustomers');
    const distributionChainIdControl = this.form.get('distributionChainId');
    const specificCustomersGroupControl = this.form.get('businessPartnerGroupId');

    this.form.get('customersSelectedsBy')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        if(this.form.get("customerSelectionType").value === 'specific'){
          if (value === 'customers') {
            specificCustomersControl?.setValidators(Validators.required);
            specificCustomersGroupControl?.clearValidators();
          } else {
            specificCustomersGroupControl?.setValidators(Validators.required);
            specificCustomersControl?.clearValidators();
          }
        }       

        specificCustomersGroupControl?.updateValueAndValidity();
        specificCustomersControl?.updateValueAndValidity();
        this._changeDetectorRef.detectChanges();
      });

    this.form.get('customerSelectionType')?.valueChanges
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(value => {
      if (value === 'all') {
        specificCustomersControl?.clearValidators();
        specificCustomersGroupControl?.clearValidators();
      } else {
        const customersSelectedsByValue = this.form.get('customersSelectedsBy')?.value;
        if (customersSelectedsByValue === 'customers') {
          specificCustomersControl?.setValidators(Validators.required);
          specificCustomersGroupControl?.clearValidators();
        } else {
          specificCustomersGroupControl?.setValidators(Validators.required);
          specificCustomersControl?.clearValidators();
        }
      }
      
      specificCustomersGroupControl?.updateValueAndValidity();
      specificCustomersControl?.updateValueAndValidity();
      this._changeDetectorRef.detectChanges();
    });

    this.form.get("customersSelectedsBy").valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {      
        if(this.form.get("customersSelectedsBy").value === 'businessPartnerGroup' && this.businessGroupList.length === 0){
          this.fetchAllBusinessGroup();
        }
      });

    this.form.get("searchBusinessPartenertGroup").valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.filterBusinessPartnerGroup(value);
      });
      
    this.customerSearchControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((query: string | null) => {
        this.findAllCustomers(query || '');
      });

    this.findAllCustomers();

    distributionChainIdControl?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this._changeDetectorRef.detectChanges();
      });

      this.customerSelectControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((selectedCustomers: BusinessPartnerAttributes[] | null) => {
        const customers = selectedCustomers ?? [];
        specificCustomersControl?.setValue(customers);
        this.selectedCustomersSubject.next(customers);
      });
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    
    this.selectedCustomersSubject
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(customers => {
        const selectedCustomers = this.customerSelectControl.value || [];

        const newCustomers = this.filteredCustomers.filter(
          searchResult => !selectedCustomers.some(
            selectedCustomer => selectedCustomer._id === searchResult._id
          )
        );

        this.filteredCustomers = [...selectedCustomers, ...newCustomers];

        this.dataSource.data = customers;
        this._changeDetectorRef.detectChanges();
      });
  }

  ngOnDestroy(): void {}

  getDisplayValueForMongoId(list: BusinessPartnerGroupAttributes[] , ids: string[], field: string): string {
      if (Array.isArray(ids)) {
        return list
          .filter(item => ids.includes(item._id.toString()))
          .map(item => item?.[field] ?? '')
          .join(', ');
      } else {
        const found = list.find(item => item._id === ids);
        return found?.[field] ?? '';
      }
    }

  filterBusinessPartnerGroup(search: string): void {
    const value = search?.toLowerCase() ?? '';
    if (value === '') {
      this.filteredBusinessGroupList = this.businessGroupList;
    } else {
      this.filteredBusinessGroupList = this.businessGroupList.filter(item =>
        item.name?.toLowerCase().includes(value)
      );
    }
  }

  findAllCustomers(searchQuery: string = ''): void {
    // Obtener los clientes que ya están seleccionados
    const selectedCustomers = this.customerSelectControl.value || [];

    this.businessService
      .findAllWithCount(
        {
          limitPerPage: 50,
          currentPage: 1,
          search: searchQuery,
          sort: 'name',
          sortDirection: 'ASC',
        },
        false,
        [],
        this.geolocationFilter,
        null,
        true
      )
      .subscribe((data) => {
        const searchResults = data.data[0].data as BusinessPartnerAttributes[];
        
        // Filtrar los resultados de la búsqueda para evitar duplicados
        const newCustomers = searchResults.filter(
          searchResult => !selectedCustomers.some(
            selectedCustomer => selectedCustomer._id === searchResult._id
          )
        );

        this.filteredCustomers = [...selectedCustomers, ...newCustomers];
        this._changeDetectorRef.detectChanges();
      });
  }

  removeSelectedCustomer(customerId: string): void {
    const currentCustomers = this.customerSelectControl.value || [];
    const updatedCustomers = currentCustomers.filter(customer => customer._id.toString() !== customerId);
    this.customerSelectControl.setValue(updatedCustomers);
  }

  fetchAllBusinessGroup(): Promise<void> {
      return new Promise((resolve, reject) => {
        this.businessGroupService.findToComboBox().subscribe({
          next: (res: IRequestResult<BusinessPartnerGroupAttributes[]>) => {
            this.businessGroupList = res.data;
            this.filteredBusinessGroupList = res.data;
            resolve();
          },
          error: (err) => {
            this.snackBar.open(err.error.message || "Error fetching business group","Cerrar",{ duration: 5000});
            reject(err);
          },
        });
      });
    }

  addCustomers(): void {
    if (this.form.valid) {
      const selectedCustomers = this.form.value.specificCustomers.map(
        (customer: { _id: string }) => customer._id
      );

      const chainIds = this.form.value.distributionChainId;

      
      const partnerChainIds: PartnerChainIds = {
        chainIds: chainIds,
        partnerIds: selectedCustomers.length > 0 ? selectedCustomers : null,
        businessPartnersGroupIds: null
      };
      
      if(this.form.get("customerSelectionType").value === 'all'){
        partnerChainIds.partnerIds = null;
        partnerChainIds.businessPartnersGroupIds = null;
      }

      if(this.form.get("customersSelectedsBy").value === 'businessPartnerGroup'){
        partnerChainIds.partnerIds = null;
        partnerChainIds.businessPartnersGroupIds = this.form.get("businessPartnerGroupId").value
      }

      this.priceListService
        .postBusinessToPriceList(this.priceListId, partnerChainIds)
        .subscribe({
          next: (res) => {
            this.snackBar.open('Listas de precios en clientes actualizadas con exito.', 'Cerrar', { duration: 3000 });
            this.dialogRef.close(true);
          },
          error: (err) => {
            this.snackBar.open(err.error.message || 'Hubo un error al actualizar listas de precios.', 'Cerrar', { duration: 3000 });
          },
        });
    } else {
      this.snackBar.open('Completar la información.', 'Cerrar', { duration: 3000 });
    }
  }

  trackByFn(index: number, item: BusinessPartnerAttributes): string {
    return item._id.toString();
  }
}